import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, BellOff, Loader2, AlertCircle, Smartphone } from "lucide-react";
import {
  disablePush, enablePush, ensureIdentity, isIOS, pushSupport, readSubscription,
} from "@/lib/push";

/**
 * "Push notifications", as a switch that tells the truth.
 *
 * Jorge put it on the dashboard rather than inside Settings because a
 * notification you are not receiving is noticed on the page you look at every
 * day, not three screens deep. Settings mirrors it beside the per-event
 * preferences, which are a different question — those say WHICH notifications,
 * this says whether this browser gets any at all.
 *
 * **State is read from the subscription, never from a local flag.** `optedIn`
 * and the subscription id are what OneSignal will act on for the next send; a
 * flag in localStorage is what we believed the last time we wrote it. They come
 * apart for ordinary reasons — data cleared, another tab, a browser reset — and
 * every one of those is a case where a stored flag shows a switch that lies.
 * Re-read on mount and after every transition, so a reload cannot disagree with
 * the switch.
 *
 * **Off is a real unsubscribe**, not a preference we consult later: OneSignal
 * stops sending and our own row goes with it. Reversible, and turning it back
 * on never asks the browser again, because the permission was never touched.
 *
 * **Denied is not a failure to hide.** The browser blocked it, no button can
 * undo that, so the switch is disabled and says where the lock icon is.
 */
export default function PushToggle({ compact = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const { state: next } = await readSubscription();
    setState(next);
    // A switch that reads "on" while OneSignal holds the device under no
    // identity is a switch that is right about delivery and wrong about
    // audiences. Same repair as the invitation card runs, from the other page
    // someone actually visits; the second caller is a no-op because
    // `ensureIdentity` collapses concurrent attempts and remembers the last one.
    if (next === "on" && user?.user_id) ensureIdentity(user.user_id);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async () => {
    setBusy(true);
    setError("");
    try {
      const result = state === "on"
        ? await disablePush()
        : await enablePush({ externalId: user?.user_id });
      if (result.state === "denied") {
        setState("denied");
      } else if (result.state === "nodialog") {
        setState("off");
        setError(t("push.toggle.nodialog"));
      } else if (result.state === "nosubscription") {
        setState("off");
        setError(t("push.toggle.nosubscription"));
      } else {
        setState(result.state);
      }
    } catch (e) {
      console.warn("[FixyDany push] toggle failed:", e);
      // The exception's own text, never. OneSignal's is `Can only be used on:
      // <origin>` — our configuration, addressed to us, and unactionable by the
      // person who just clicked a switch. The console gets the detail; the
      // switch says the one true thing, that it did not take.
      setError(t("push.toggle.failed"));
      // Whatever the attempt did, the subscription is the authority on where we
      // ended up — never leave the switch showing the state we hoped for.
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const support = pushSupport();
  if (support === "unconfigured") return null;

  // iOS gained web push in 16.4 and gave it only to sites launched from the
  // home screen. Asking for permission inside Safari is impossible, so the card
  // asks for the step that makes it possible instead of showing a dead switch.
  if (state === "ios-needs-install" || support === "ios-needs-install") {
    return (
      <Shell compact={compact} testid="push-toggle-ios">
        <Row
          icon={<Smartphone size={16} className="text-hive-ink" />}
          title={t("push.toggle.title")}
          body={t("push.toggle.iosInstall")}
        />
      </Shell>
    );
  }
  if (state === "unsupported" || support === "unsupported") {
    return (
      <Shell compact={compact} testid="push-toggle-unsupported">
        <Row
          icon={<BellOff size={16} className="text-smoke" />}
          title={t("push.toggle.title")}
          body={t("push.toggle.unsupported")}
        />
      </Shell>
    );
  }

  const on = state === "on";
  const denied = state === "denied";
  const loading = state === "loading";

  return (
    <Shell compact={compact} testid="push-toggle">
      <div className="flex items-start justify-between gap-4">
        <Row
          icon={on ? <Bell size={16} className="text-hive-ink" />
                   : <BellOff size={16} className="text-smoke" />}
          title={t("push.toggle.title")}
          body={
            denied ? t("push.toggle.blocked")
              : on ? t("push.toggle.onBody")
              : t("push.toggle.offBody")
          }
        />
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={t("push.toggle.title")}
          disabled={busy || denied || loading}
          onClick={toggle}
          data-testid="push-toggle-switch"
          data-state={denied ? "denied" : loading ? "loading" : on ? "on" : "off"}
          className={[
            "relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition",
            "border-[1.5px] border-hive disabled:opacity-40 disabled:cursor-not-allowed",
            on ? "bg-hive" : "bg-sand",
          ].join(" ")}
        >
          <span
            className={[
              "inline-block h-4 w-4 transform rounded-full bg-cream transition",
              on ? "translate-x-6" : "translate-x-1",
            ].join(" ")}
          />
          {busy && (
            <Loader2
              size={12}
              className="absolute -right-5 animate-spin text-smoke"
              data-testid="push-toggle-busy"
            />
          )}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-xs text-red-700 flex items-start gap-1"
           data-testid="push-toggle-error">
          <AlertCircle size={13} className="shrink-0 mt-px" />{error}
        </p>
      )}
    </Shell>
  );
}

function Shell({ children, compact, testid }) {
  return (
    <div
      className={compact ? "" : "card-base p-4"}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function Row({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-hive/20 rounded-md p-2 shrink-0">{icon}</div>
      <div>
        <p className="font-display font-bold text-worker text-sm">{title}</p>
        <p className="mt-1 text-xs text-smoke leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// Exported for the opt-in card, which needs the same platform answer without
// rendering a switch.
export { isIOS };
